/**
 * Mark-to-market open positions from live Hyperliquid mids.
 * Clearinghouse snapshots (and userEvents) do not tick with mark; allMids does.
 */

export type LiveMarkSide = 'long' | 'short' | 'LONG' | 'SHORT' | string;

export type LiveMarkablePosition = {
  pair: string;
  side: LiveMarkSide;
  size: number;
  entryPrice: number;
  currentPrice: number;
  pnl: number;
  pnlPercent: number;
  notionalValue: number;
  marginUsed: number;
};

export type LiveMarkablePortfolio = {
  equity: number;
  positions: LiveMarkablePosition[];
  totalUnrealizedPnl: number;
  totalNtlPos?: number;
};

const DEX_QUOTE: Record<string, string> = {
  XYZ: 'USDC',
  CASH: 'USDT',
  KM: 'USDH',
  PARA: 'USDC',
};

export function parseHlDexCoinKey(raw: string): { dex: string; sym: string } | null {
  const parts = String(raw || '')
    .split(':')
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => p.toUpperCase());
  if (parts.length === 2) return { dex: parts[0], sym: parts[1] };
  if (parts.length === 3 && parts[0] === 'A') return { dex: parts[1], sym: parts[2] };
  if (parts.length >= 4 && parts[0] === 'A' && parts[1] === 'DEX') return { dex: parts[2], sym: parts[3] };
  return null;
}

function quoteForDex(dex: string): string {
  return DEX_QUOTE[String(dex || '').toUpperCase()] || 'USDC';
}

function parseMidPx(raw: unknown): number {
  const n = typeof raw === 'string' ? parseFloat(raw) : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : NaN;
}

/** REST allMids is a flat map; WS often wraps `{ mids: { … }, dex?: string }`. */
export function unwrapHlAllMidsPayload(raw: unknown): { mids: Record<string, unknown>; dex: string } {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { mids: {}, dex: '' };
  const obj = raw as Record<string, unknown>;
  const wrapped = obj.mids && typeof obj.mids === 'object' && !Array.isArray(obj.mids)
    ? (obj.mids as Record<string, unknown>)
    : obj;
  const dex = String(obj.dex || '').trim().toLowerCase();
  return { mids: wrapped, dex };
}

function writeMid(prices: Map<string, number>, key: string, px: number): void {
  const k = String(key || '').toUpperCase().trim();
  if (!k || !(px > 0)) return;
  prices.set(k, px);
}

/** Merge one allMids payload into a canonical pair→px map (core: COIN, HIP-3: COIN-QUOTE). */
export function ingestHlAllMids(
  prices: Map<string, number>,
  raw: unknown,
  streamDex?: string,
): void {
  const { mids, dex: payloadDex } = unwrapHlAllMidsPayload(raw);
  const stream = String(payloadDex || streamDex || '').trim().toLowerCase();
  for (const [rawKey, rawVal] of Object.entries(mids)) {
    const px = parseMidPx(rawVal);
    if (!(px > 0)) continue;
    const keyUpper = String(rawKey || '').toUpperCase().trim();
    if (!keyUpper || keyUpper === 'MIDS' || keyUpper === 'DEX') continue;

    if (keyUpper.includes(':')) {
      const parsed = parseHlDexCoinKey(keyUpper);
      if (!parsed?.sym) continue;
      const quote = quoteForDex(parsed.dex);
      writeMid(prices, `${parsed.sym}-${quote}`, px);
      writeMid(prices, `${parsed.dex}:${parsed.sym}`, px);
      continue;
    }

    if (stream) {
      writeMid(prices, `${keyUpper}-${quoteForDex(stream)}`, px);
      writeMid(prices, `${stream.toUpperCase()}:${keyUpper}`, px);
      continue;
    }

    writeMid(prices, keyUpper, px);
  }
}

export function lookupPositionMark(prices: Map<string, number>, pair: string): number {
  const raw = String(pair || '').toUpperCase().trim();
  if (!raw || prices.size === 0) return NaN;
  const noPerp = raw.replace(/-PERP$/i, '');
  const colon = noPerp.indexOf(':');
  const dexPrefixed = colon > 0 ? noPerp.slice(colon + 1) : noPerp;
  const base = dexPrefixed.includes('-') ? dexPrefixed.split('-')[0] : dexPrefixed;
  const tryKeys = [
    raw,
    noPerp,
    dexPrefixed,
    `${base}-USDC`,
    `${base}-USDT`,
    `${base}-USDH`,
    `${base}-USD`,
    base,
  ];
  for (const k of tryKeys) {
    const n = prices.get(k);
    if (Number.isFinite(n) && (n as number) > 0) return n as number;
  }
  return NaN;
}

export function liveUnrealizedPnlUsd(
  size: number,
  side: LiveMarkSide,
  entry: number,
  mark: number,
): number {
  const sz = Math.abs(Number(size) || 0);
  if (!(sz > 0) || !(entry > 0) || !(mark > 0)) return NaN;
  const short = String(side || '').toLowerCase().startsWith('s');
  return short ? sz * (entry - mark) : sz * (mark - entry);
}

export function livePnlPercent(
  pnlUsd: number,
  size: number,
  entry: number,
  marginUsed = 0,
): number {
  if (!Number.isFinite(pnlUsd)) return 0;
  if (marginUsed > 0) return (pnlUsd / marginUsed) * 100;
  const notional = Math.abs(Number(size) || 0) * (Number(entry) || 0);
  if (notional > 0) return (pnlUsd / notional) * 100;
  return 0;
}

const PNL_EPS = 0.005;

export function applyLiveMarksToPortfolio<T extends LiveMarkablePortfolio>(
  portfolio: T,
  prices: Map<string, number>,
): T | null {
  if (!portfolio?.positions?.length || prices.size === 0) return null;
  let changed = false;
  const nextPositions = portfolio.positions.map((p) => {
    const mark = lookupPositionMark(prices, p.pair);
    if (!(mark > 0)) return p;
    const pnl = liveUnrealizedPnlUsd(p.size, p.side, p.entryPrice, mark);
    if (!Number.isFinite(pnl)) return p;
    if (
      Math.abs(mark - (Number(p.currentPrice) || 0)) < 1e-12 &&
      Math.abs(pnl - (Number(p.pnl) || 0)) < PNL_EPS
    ) {
      return p;
    }
    changed = true;
    const pnlPercent = livePnlPercent(pnl, p.size, p.entryPrice, Number(p.marginUsed) || 0);
    return {
      ...p,
      currentPrice: mark,
      pnl,
      pnlPercent,
      notionalValue: Math.abs(Number(p.size) || 0) * mark,
    };
  });
  if (!changed) return null;
  const totalUnrealizedPnl = nextPositions.reduce((s, p) => s + (Number(p.pnl) || 0), 0);
  const delta = totalUnrealizedPnl - (Number(portfolio.totalUnrealizedPnl) || 0);
  const totalNtlPos = nextPositions.reduce((s, p) => s + (Number(p.notionalValue) || 0), 0);
  return {
    ...portfolio,
    positions: nextPositions,
    totalUnrealizedPnl,
    equity: (Number(portfolio.equity) || 0) + delta,
    totalNtlPos,
  };
}

/** Overlay a live mark onto Mini App / Oroboros position row shapes. */
export function withLiveMark<T extends Record<string, any>>(pos: T, mark: number): T {
  const px = Number(mark);
  if (!(px > 0) || !pos) return pos;
  const size = Math.abs(Number(pos.size) || 0);
  const entry = Number(pos.entryPrice) || 0;
  const pnl = liveUnrealizedPnlUsd(size, pos.side, entry, px);
  if (!Number.isFinite(pnl)) return pos;
  const pct = livePnlPercent(pnl, size, entry, Number(pos.marginUsed) || 0);
  return {
    ...pos,
    currentPrice: px,
    markPrice: px,
    pnl,
    unrealizedPnL: pnl,
    pnlPercent: pct,
    unrealizedPnLPercent: pct,
    notionalValue: size * px,
  };
}
